import { graphql } from 'gql.tada';
import { useQuery } from '@apollo/client';

/**
 * Everything the settings page needs, in one request.
 *
 * The overview summarizes every area at once ("3 tuition plans", "2026-27 has
 * no terms"), so splitting this per section would mean the landing view firing
 * seven queries to draw one screen. Prices belong to a school year, so they
 * come with each year.
 */
const SchoolSettingsQuery = graphql.persisted(
  "SchoolSettingsQuery",
  graphql(`
    query SchoolSettingsQuery($schoolId: ID!) {
      school(id: $schoolId) {
        id
        name
        schoolYears {
          id
          name
          slug
          state
          termsCount
          tuitionOptionsSetting {
            description
            items {
              key
              name
              costCents
              description
            }
          }
          tuitionPaymentOptionsSetting {
            key
            name
            description
            scheduleType
            billingDayOfMonth
            startMonth
            discountCents
            discountType
            appliesTo
            selectableOnEnrollment
          }
          enrollmentFeeSetting {
            name
            description
            amountCents
          }
        }
        classrooms {
          id
        }
        gradingTemplates {
          id
          weightTotal
        }
        gradeLevelsSetting {
          description
          data
          schema
          items {
            description
            order
          }
        }
        gradeScaleSetting {
          entries {
            letter
            minimumPercentage
            gpaPoints
            color
          }
        }
        parentAlertsSetting {
          enabled
          invoiceEmails
          invoiceEmailsSince
          invoiceOverdue
          invoiceDue
          enrollmentAction
          missingWork
          lowGrade
          showGradeColors
          lowGradeFloorLetter
          lowGradeFloorPercentage
        }
        paymentSettings
        stripeAccount {
          id
          status
          stripeAccountId
          connected
          active
          chargesEnabled
          payoutsEnabled
          onboardingComplete
        }
      }
    }
  `)
);

export { SchoolSettingsQuery };

export function useSchoolSettingsQuery(schoolId: string | undefined) {
  return useQuery(SchoolSettingsQuery, {
    variables: { schoolId: schoolId! },
    skip: !schoolId,
  });
}
